import { useMemo, useState } from "react";
import { Button, TextField, FormControl, Popover } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Custom Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";

//Date utils
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

// Constants
import {
  GeneralTaskFrequency,
  DAYS_OF_WEEK,
  DAYS_OF_MONTH,
  DATE_FORMAT,
  GeneralTaskStatus,
} from "constant/GeneralTaskType";

export default function LXOGeneralTaskForm(props) {
  const {
    action,
    item,
    onSubmit,
    onClose,
    state,
    setState,
    validate,
    error,
    category,
    frequency,
    statusList,
  } = props;

  const currentStatus = statusList.find(
    (status) => status.value === state.status
  );

  const allowStatusList = useMemo(() => {
    return statusList.filter((status) => {
      const currentStatusValue = state.status;
      const excludeStatus = [GeneralTaskStatus.COMPLETE, currentStatusValue];
      return !excludeStatus.includes(status.value);
    });
  }, [statusList, state.status]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    if (
      state.status === GeneralTaskStatus.COMPLETE ||
      state.status === GeneralTaskStatus.CANCEL
    ) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "status-list-popover" : undefined;

  return (
    <MDBox>
      {action === "delete" ? (
        <>
          <MDBox className="modal-leave-application-content">
            <MDTypography variant="body2">
              Confirm to delete task {`${item.title || "this task"}`}?
            </MDTypography>
          </MDBox>
          <MDBox className="modal-leave-application-footer">
            <Button
              sx={{ textTransform: "none" }}
              variant="outlined"
              onClick={onClose}
              className="cancel-button"
            >
              Cancel
            </Button>
            <Button
              sx={{ textTransform: "none" }}
              className="submit-button"
              variant="contained"
              color="primary"
              onClick={() => onSubmit(item)}
            >
              Delete
            </Button>
          </MDBox>
        </>
      ) : (
        <>
          <MDBox className="modal-leave-application-content">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <MDBox className="modal-leave-application-form">
                <MDBox className="modal-leave-application-form-group">
                  <MDTypography variant="subtitle2">
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <span>
                        Title <sup>{error.title ? "*Required" : "*"}</sup>
                      </span>

                      {action === "update" && (
                        <div>
                          <Button
                            disabled={
                              state.status === GeneralTaskStatus.COMPLETE ||
                              state.status === GeneralTaskStatus.CANCEL
                            }
                            aria-describedby={id}
                            variant="contained"
                            color="info"
                            onClick={handleClick}
                            size="small"
                            sx={{
                              textTransform: "none",
                              marginBottom: "10px",
                              background: currentStatus?.background || "#aaa",
                              color: currentStatus?.color || "#fff",
                              "&:hover": {
                                opacity: 0.8,
                                background: currentStatus?.background || "#aaa",
                                color: currentStatus?.color || "#fff",
                              },
                            }}
                            endIcon={<KeyboardArrowDownIcon />}
                          >
                            {currentStatus?.label || "Loading..."}
                          </Button>
                          <Popover
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            backgroundColor="#fff"
                          >
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              gap={1}
                              background="#fff"
                            >
                              {allowStatusList.map((status) => (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  sx={{
                                    width: "120px",
                                    textTransform: "none",
                                    background: status.background || "#aaa",
                                    color: status.color || "#fff",
                                    "&:hover": {
                                      opacity: 0.8,
                                      background: status.background || "#aaa",
                                      color: status.color || "#fff",
                                    },
                                  }}
                                  key={status.value}
                                  onClick={() => {
                                    setState({
                                      ...state,
                                      status: status.value,
                                    });
                                    handleClose();
                                  }}
                                >
                                  {status.label}
                                </Button>
                              ))}
                            </MDBox>
                          </Popover>
                        </div>
                      )}
                    </MDBox>
                  </MDTypography>
                  <TextField
                    className="text-input title-input"
                    fullWidth
                    placeholder="Enter task title"
                    value={state.title}
                    onChange={(e) =>
                      setState({ ...state, title: e.target.value })
                    }
                  />
                </MDBox>

                <MDBox sxclassName="modal-leave-application-form-group">
                  <MDTypography variant="subtitle2">
                    Code <sup>{error.code ? "*Required" : "*"}</sup>
                  </MDTypography>
                  <TextField
                    fullWidth
                    placeholder="Enter task code"
                    value={state.code}
                    onChange={(e) =>
                      setState({ ...state, code: e.target.value })
                    }
                  />
                </MDBox>

                <MDBox className="modal-leave-application-form-group">
                  <MDTypography variant="subtitle2">
                    Category <sup>{error.category ? "*Required" : "*"}</sup>
                  </MDTypography>
                  <FormControl fullWidth>
                    <MDSelect
                      className="select-input category-select"
                      options={category}
                      value={state.category}
                      onChange={(value) =>
                        setState({ ...state, category: value })
                      }
                      placeholder="Select category"
                    />
                  </FormControl>
                </MDBox>

                <MDBox className="modal-leave-application-form-dates">
                  <MDBox
                    sx={{ flex: 1, pr: { xs: 0, sm: 1 } }}
                    className="frequency"
                  >
                    <MDTypography variant="subtitle2">
                      Frequency <sup>{error.frequency ? "*Required" : "*"}</sup>
                    </MDTypography>
                    <FormControl fullWidth>
                      <MDSelect
                        className="select-input frequency-select"
                        options={frequency}
                        value={state.frequency}
                        onChange={(value) =>
                          setState({
                            ...state,
                            frequency: value,
                            recurrenceDay: null,
                          })
                        }
                        placeholder="Select frequency"
                      />
                    </FormControl>
                  </MDBox>
                  <MDBox
                    sx={{ flex: 1, pl: { xs: 0, sm: 1 } }}
                    className="recurrence-day"
                  >
                    {!state.frequency ? (
                      <>
                        <MDTypography variant="subtitle2" sx={{ opacity: 0 }}>
                          Recurrence Day
                        </MDTypography>
                        <TextField
                          className="text-input recurrence-day-input"
                          placeholder="Please select frequency first"
                          fullWidth
                          disabled
                        />
                      </>
                    ) : (
                      <>
                        {[
                          GeneralTaskFrequency.WEEKLY,
                          GeneralTaskFrequency.MONTHLY,
                        ].includes(state.frequency?.value) && (
                          <>
                            <MDTypography variant="subtitle2">
                              {state.frequency?.value ===
                              GeneralTaskFrequency.WEEKLY
                                ? "Day of Week"
                                : "Day of Month"}{" "}
                              <sup>
                                {error.recurrenceDay ? "*Required" : "*"}
                              </sup>
                            </MDTypography>

                            {state.frequency?.value ===
                            GeneralTaskFrequency.WEEKLY ? (
                              <FormControl fullWidth>
                                <MDSelect
                                  className="select-input recurrence-day-select"
                                  options={DAYS_OF_WEEK}
                                  value={state.recurrenceDay}
                                  onChange={(value) =>
                                    setState({
                                      ...state,
                                      recurrenceDay: value,
                                    })
                                  }
                                  placeholder="Select day of week"
                                />
                              </FormControl>
                            ) : (
                              <FormControl fullWidth>
                                <MDSelect
                                  isSearchable
                                  className="select-input recurrence-day-select"
                                  options={DAYS_OF_MONTH}
                                  value={state.recurrenceDay}
                                  onChange={(value) =>
                                    setState({
                                      ...state,
                                      recurrenceDay: value,
                                    })
                                  }
                                  placeholder="Select day of month"
                                />
                              </FormControl>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </MDBox>
                </MDBox>

                <MDBox className="modal-leave-application-form-dates">
                  <MDBox sx={{ flex: 1, pr: { xs: 0, sm: 1 } }}>
                    <MDTypography variant="subtitle2">
                      From Date <sup>{error.fromDate ? "Required" : "*"}</sup>
                    </MDTypography>
                    <DatePicker
                      className="date-time-picker"
                      value={state.fromDate}
                      onChange={(newValue) =>
                        setState({
                          ...state,
                          fromDate: newValue,
                        })
                      }
                      format={DATE_FORMAT}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: DATE_FORMAT,
                        },
                      }}
                    />
                  </MDBox>
                  <MDBox sx={{ flex: 1, pl: { xs: 0, sm: 1 } }}>
                    <MDTypography variant="subtitle2">
                      To Date <sup>{error.toDate ? "Required" : "*"}</sup>
                    </MDTypography>
                    <DatePicker
                      className="date-time-picker"
                      value={state.toDate}
                      onChange={(newValue) =>
                        setState({
                          ...state,
                          toDate: newValue,
                        })
                      }
                      format={DATE_FORMAT}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: DATE_FORMAT,
                        },
                      }}
                    />
                  </MDBox>
                </MDBox>

                <MDBox className="modal-leave-application-form-dates">
                  <MDBox sx={{ flex: 1, pr: { xs: 0, sm: 1 } }}>
                    <MDTypography variant="subtitle2">
                      Start Time <sup>{error.startTime ? "Required" : "*"}</sup>
                    </MDTypography>
                    <TimePicker
                      className="date-time-picker"
                      value={state.startTime}
                      ampm={false}
                      onChange={(newValue) => {
                        if (newValue) {
                          const now = moment(); // ngày hiện tại
                          const updated = moment(newValue).set({
                            year: now.year(),
                            month: now.month(),
                            date: now.date(),
                          });

                          setState({
                            ...state,
                            startTime: updated,
                          });
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </MDBox>
                  <MDBox sx={{ flex: 1, pl: { xs: 0, sm: 1 } }}>
                    <MDTypography variant="subtitle2">
                      End Time <sup>{error.endTime ? "Required" : "*"}</sup>
                    </MDTypography>
                    <TimePicker
                      className="date-time-picker"
                      value={state.endTime}
                      ampm={false}
                      onChange={(newValue) => {
                        if (newValue) {
                          const now = moment(); // ngày hiện tại
                          const updated = moment(newValue).set({
                            year: now.year(),
                            month: now.month(),
                            date: now.date(),
                          });

                          setState({
                            ...state,
                            endTime: updated,
                          });
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </MDBox>
                </MDBox>
              </MDBox>
            </LocalizationProvider>
          </MDBox>
          <MDBox className="modal-leave-application-footer">
            <Button
              sx={{ textTransform: "none" }}
              variant="outlined"
              onClick={onClose}
              className="cancel-button"
            >
              Cancel
            </Button>
            <Button
              disabled={
                state.status === GeneralTaskStatus.COMPLETE ||
                state.status === GeneralTaskStatus.CANCEL
              }
              sx={{ textTransform: "none" }}
              className="submit-button"
              variant="contained"
              color="primary"
              onClick={() => {
                if (validate()) {
                  const payload = {
                    ...state,
                  };
                  onSubmit(payload);
                }
              }}
            >
              {action === "create" ? "Create" : "Update"}
            </Button>
          </MDBox>
        </>
      )}
    </MDBox>
  );
}
